import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import type { ManuscriptToken } from "../api/types";
import { formatTime } from "../lib/format";
import type { FindHit } from "../lib/find";
import { usePageFind } from "../lib/find";
import { pagePicture } from "../lib/pageView";
import { HighlightCtor, registry, tokenRange, useTokenIndex } from "./ScriptMarksLayer";
import { Icon } from "./icons";

/**
 * The report's Find mode ("Find all of these in the manuscript"): every place
 * a word or phrase is in the book, one after another, across files.
 *
 * - FindBar: what you're finding, how many, ↑/↓ through them, Done;
 * - FindRail: the pages they're on, as thumbnails of the uploaded pages,
 *   grouped by file - like a word processor's page list;
 * - FindPanel: every hit with its words around it, heard ticks and flags,
 *   the one you're on opened up to play, flag, or edit the flags on it;
 * - useFindHighlights: the hits picked out in the manuscript text, without
 *   touching it (CSS Custom Highlight API, as the script's marks are).
 *
 * The report follows the hit you're on: moving to one in another file opens
 * that file, so its manuscript, flags and audio are the ones you act on.
 */

/** What the manuscript panel is told: this file's hits and the one you're on. */
export interface PaneFind {
  hits: { id: string; tokens: number[] }[];
  current: string | null;
  tick: number;                 // bumped on every move, so a re-visit re-scrolls
}

/** One hit's state, for its row and thumbnail. */
export interface HitState {
  heard: boolean;
  flagged: boolean;             // flagged by you here, or a flag on it picked up
  chips: { label: string; cls: string }[];
}

const fileLabel = (h: FindHit) => h.file ?? "Not in a proofed file";

// --------------------------------------------------------------------------- //
// The hits in the text                                                         //
// --------------------------------------------------------------------------- //

export function useFindHighlights(
  rootRef: RefObject<HTMLDivElement>, tokens: ManuscriptToken[], find: PaneFind | undefined, on: boolean,
  deps: unknown[],
) {
  const byGlobal = useTokenIndex(tokens);
  const ranges = useRef<Range[]>([]);
  const [stale, setStale] = useState(0);

  // A redraw that replaced the text the ranges point into: draw them again.
  useLayoutEffect(() => {
    if (ranges.current.some((r) => !r.startContainer.isConnected)) setStale((n) => n + 1);
  });

  useLayoutEffect(() => {
    const reg = registry();
    const H = HighlightCtor();
    const root = rootRef.current;
    if (!reg || !H) return;
    reg.delete("find-hit");
    reg.delete("find-now");
    ranges.current = [];
    if (!root || !find || !on) return;
    const all: Range[] = [];
    const now: Range[] = [];
    for (const h of find.hits) {
      for (const i of h.tokens) {
        const tok = byGlobal.get(i);
        const r = tok ? tokenRange(root, tok) : null;
        if (r) (h.id === find.current ? now : all).push(r);
      }
    }
    ranges.current = [...all, ...now];
    if (all.length) reg.set("find-hit", new H(...all));
    if (now.length) {
      const hl = new H(...now) as { priority?: number };
      hl.priority = 2;                       // over the script's own highlights
      reg.set("find-now", hl);
    }
  }, [find, on, byGlobal, stale, ...deps]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    const reg = registry();
    reg?.delete("find-hit");
    reg?.delete("find-now");
  }, []);
}

// --------------------------------------------------------------------------- //
// The bar                                                                       //
// --------------------------------------------------------------------------- //

export function FindBar({ query, hits, index, loading, truncated, autoplay, onAutoplay, onSearch, onPrev, onNext, onDone }: {
  query: string;
  hits: FindHit[];
  index: number;
  loading: boolean;
  truncated: boolean;
  autoplay: boolean;
  onAutoplay: (on: boolean) => void;
  onSearch: (q: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);
  const pages = useMemo(() => new Set(hits.map((h) => h.pdf_page)).size, [hits]);
  const files = useMemo(() => new Set(hits.filter((h) => h.chapter_id).map((h) => h.chapter_id)).size, [hits]);
  const n = hits.length;
  return (
    <div className="find-bar" role="search">
      <Icon name="search" size={17} className="find-bar-ic" />
      <form className="find-q" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) onSearch(draft.trim()); }}>
        <label htmlFor="find-q">Every</label>
        <input id="find-q" className="input" value={draft} onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (draft.trim() && draft.trim() !== query) onSearch(draft.trim()); }}
          aria-label="What to find" />
      </form>
      <span className="find-count">
        {loading ? "Searching the book…"
          : n === 0 ? "Not found in any proofed file"
          : <><b>{n}{truncated ? "+" : ""}</b> in the book · on {pages} {pages === 1 ? "page" : "pages"}
            {files > 0 && <> · in {files} {files === 1 ? "file" : "files"}</>}</>}
      </span>
      <span className="find-grow" />
      <span className="find-step">
        <button type="button" className="find-step-btn" onClick={onPrev} disabled={index <= 0} aria-label="Previous one (↑)" title="Previous one (↑)">
          <Icon name="chevron-up" size={16} />
        </button>
        <span className="find-pos"><b>{index >= 0 ? index + 1 : "–"}</b> of {n}</span>
        <button type="button" className="find-step-btn" onClick={onNext} disabled={index >= n - 1} aria-label="Next one (↓)" title="Next one (↓)">
          <Icon name="chevron-down" size={16} />
        </button>
      </span>
      <label className="find-auto">
        <input type="checkbox" checked={autoplay} onChange={(e) => onAutoplay(e.target.checked)} />
        Play each as I move
      </label>
      <button type="button" className="btn sm find-done" onClick={onDone} title="Leave Find mode (Esc)">
        Done <kbd>Esc</kbd>
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// The pages                                                                     //
// --------------------------------------------------------------------------- //

interface PageGroup {
  key: string;
  file: string;                 // chapter id, or "none"
  label: string;
  count: number;
  pages: { pdf: number | null; shown: number | null; hits: number[] }[];   // hit indices, in order
}

function groupPages(hits: FindHit[]): PageGroup[] {
  const out: PageGroup[] = [];
  hits.forEach((h, i) => {
    const file = h.chapter_id ?? "none";
    let g = out[out.length - 1];
    if (!g || g.file !== file) {
      g = { key: `${file}#${out.length}`, file, label: fileLabel(h), count: 0, pages: [] };
      out.push(g);
    }
    g.count++;
    const last = g.pages[g.pages.length - 1];
    if (last && last.pdf === h.pdf_page) last.hits.push(i);
    else g.pages.push({ pdf: h.pdf_page, shown: h.page, hits: [i] });
  });
  return out;
}

export function FindRail({ projectId, query, hits, index, fp, sizes, stateOf, onGo }: {
  projectId: string;
  query: string;
  hits: FindHit[];
  index: number;
  fp: string | undefined;
  sizes: [number, number][] | undefined;
  stateOf: (h: FindHit) => HitState;
  onGo: (i: number) => void;
}) {
  const groups = useMemo(() => groupPages(hits), [hits]);
  const railRef = useRef<HTMLDivElement>(null);
  const cur = hits[index];

  // Keep the page you're on in sight.
  useEffect(() => {
    const el = railRef.current?.querySelector<HTMLElement>(".fr-page.now");
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [index]);

  return (
    <nav className="find-rail" ref={railRef} aria-label={`Pages with “${query}”`}>
      <div className="fr-head">Pages with “{query}”</div>
      {groups.map((g) => (
        <section key={g.key} className="fr-group">
          <div className="fr-ghead">
            <span className="fr-file">{g.label}</span>
            <span className="fr-gcount">{g.count} on {g.pages.length} {g.pages.length === 1 ? "page" : "pages"}</span>
          </div>
          {g.pages.map((p) => (
            <RailPage key={`${p.pdf}-${p.hits[0]}`} projectId={projectId} query={query} page={p} hits={hits}
              size={p.pdf != null ? sizes?.[p.pdf - 1] : undefined} fp={fp} root={railRef}
              now={!!cur && p.hits.includes(index)} index={index} stateOf={stateOf} onGo={onGo} />
          ))}
        </section>
      ))}
    </nav>
  );
}

function RailPage({ projectId, query, page, hits, size, fp, root, now, index, stateOf, onGo }: {
  projectId: string;
  query: string;
  page: PageGroup["pages"][number];
  hits: FindHit[];
  size: [number, number] | undefined;
  fp: string | undefined;
  root: RefObject<HTMLDivElement>;
  now: boolean;
  index: number;
  stateOf: (h: FindHit) => HitState;
  onGo: (i: number) => void;
}) {
  // Asked for only once it scrolls near: a long name can be on a hundred pages.
  const ref = useRef<HTMLButtonElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) setSeen(true); },
      { root: root.current, rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [root, seen]);
  const { data } = usePageFind(projectId, page.pdf, query, seen && !!fp);
  const states = page.hits.map((i) => stateOf(hits[i]));
  const heard = states.filter((s) => s.heard).length;
  const timed = page.hits.filter((i) => hits[i].start != null).length;
  // The k-th box on the page is the k-th hit on it - when the two counts agree.
  const boxes = data?.boxes && data.boxes.length === page.hits.length ? data.boxes : null;
  const ratio = size ? `${size[0]} / ${size[1]}` : "2 / 3";
  const target = page.hits.includes(index) ? index : page.hits[0];
  return (
    <div className={`fr-page-wrap${now ? " now" : ""}`}>
      <button ref={ref} type="button" className={`fr-page${now ? " now" : ""}${timed === 0 ? " silent" : ""}`}
        style={{ aspectRatio: ratio }} onClick={() => onGo(target)}
        aria-label={`Page ${page.shown ?? page.pdf}: ${page.hits.length} found`} aria-current={now ? "page" : undefined}>
        {seen && fp && page.pdf != null && (
          <img src={pagePicture(projectId, page.pdf, 300, fp)} alt="" loading="lazy" decoding="async" draggable={false} />
        )}
        {(boxes ?? data?.boxes ?? []).map((rects, k) => {
          const i = boxes ? page.hits[k] : -1;
          const cls = i === index ? " now" : i >= 0 && states[k]?.flagged ? " flagged" : "";
          return rects.map((r, j) => (
            <span key={`${k}-${j}`} className={`fr-box${cls}`}
              style={{ left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%` }} />
          ));
        })}
        <span className="fr-n">{page.hits.length}</span>
      </button>
      <span className="fr-label">
        p. {page.shown ?? page.pdf}
        {timed > 0 && (heard >= timed
          ? <span className="fr-done" role="img" aria-label="All heard"><Icon name="check" size={12} /></span>
          : heard > 0 ? <span className="fr-heard"> · {heard} of {timed} heard</span> : null)}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// The list                                                                      //
// --------------------------------------------------------------------------- //

export function FindPanel({ hits, index, loading, stateOf, note, onNote, onGo, current }: {
  hits: FindHit[];
  index: number;
  loading: boolean;
  stateOf: (h: FindHit) => HitState;
  note: string;
  onNote: (s: string) => void;
  onGo: (i: number) => void;
  current: ReactNode;            // the hit you're on, opened up (its actions and flags)
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const states = useMemo(() => hits.map(stateOf), [hits, stateOf]);
  const timed = hits.filter((h) => h.start != null).length;
  const heard = states.filter((s, i) => s.heard && hits[i].start != null).length;
  const flagged = states.filter((s) => s.flagged).length;

  // Keep the row you're on in sight.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(".fp-row.now");
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [index]);

  return (
    <div className="find-panel">
      <div className="fp-head">
        <label className="fp-note">
          Note on each flag you add
          <input className="input" value={note} placeholder="e.g. Should be NEE-na, not NYE-na"
            onChange={(e) => onNote(e.target.value)} />
        </label>
        {timed > 0 && (
          <div className="fp-progress">
            <span><b>{heard}</b> of {timed} heard</span>
            <span className="fp-bar" aria-hidden="true"><span style={{ width: `${(heard / timed) * 100}%` }} /></span>
            {flagged > 0 && <span className="fp-flagged"><Icon name="flag" size={12} /> {flagged} flagged</span>}
          </div>
        )}
      </div>
      <div className="fp-list scroll" ref={listRef}>
        {loading && <div className="muted fp-empty">Searching the book…</div>}
        {!loading && hits.length === 0 && (
          <div className="muted fp-empty">Not found in any proofed file. Try fewer words, or check the spelling.</div>
        )}
        {hits.map((h, i) => {
          const head = i === 0 || hits[i - 1].chapter_id !== h.chapter_id;
          return (
            <div key={h.id}>
              {head && <FileHead hits={hits} from={i} />}
              {i === index
                ? <div className="fp-row now" data-hit={h.id}>{current}</div>
                : <HitRow h={h} n={i + 1} state={states[i]} onGo={() => onGo(i)} />}
            </div>
          );
        })}
      </div>
      <div className="fp-keys">
        <span><kbd>↓</kbd> next</span>
        <span><kbd>↑</kbd> previous</span>
        <span><kbd>Enter</kbd> play</span>
        <span><kbd>F</kbd> flag</span>
        <span><kbd>Esc</kbd> done</span>
      </div>
    </div>
  );
}

function FileHead({ hits, from }: { hits: FindHit[]; from: number }) {
  const h = hits[from];
  let to = from;
  while (to + 1 < hits.length && hits[to + 1].chapter_id === h.chapter_id) to++;
  const a = h.page, b = hits[to].page;
  return (
    <div className="fp-file">
      <span className="fp-file-name">{fileLabel(h)}</span>
      <span>{a === b ? `p. ${a}` : `pp. ${a}–${b}`} · {to - from + 1}</span>
    </div>
  );
}

/** The last `n` words of `s`, its trailing space kept ("…told Nina. "). */
function lastWords(s: string, n: number): string {
  const trail = /\s*$/.exec(s)?.[0] ?? "";
  const words = s.trimEnd().split(/\s+/).filter(Boolean);
  return words.length > n ? `…${words.slice(-n).join(" ")}${trail}` : s;
}

/** The words around a hit as printed (the server keeps their spacing and
 *  punctuation), the hit in bold. `short`: a row's one line, where a long
 *  lead-in would push the hit itself out of sight. */
export function HitWords({ h, mark, short }: { h: FindHit; mark?: boolean; short?: boolean }) {
  return (
    <>
      {short ? lastWords(h.before, 4) : h.before}
      {mark ? <mark className="fp-word">{h.text}</mark> : <b>{h.text}</b>}
      {h.after}
    </>
  );
}

const HitRow = memo(function HitRow({ h, n, state, onGo }: { h: FindHit; n: number; state: HitState; onGo: () => void }) {
  return (
    <button type="button" className={`fp-row${h.start == null ? " silent" : ""}`} data-hit={h.id} onClick={onGo}>
      <span className="fp-n">{n}</span>
      <span className="fp-body">
        <span className="fp-meta">
          <span>p. {h.page ?? "–"}</span>
          {h.start != null ? <span className="mono">{formatTime(h.start)}</span> : <span>No audio yet</span>}
          <span className="find-grow" />
          {state.chips.map((c) => <span key={c.label} className={`fp-chip ${c.cls}`}>{c.label}</span>)}
          {state.heard && h.start != null && <span className="fp-heard"><Icon name="check" size={12} /> Heard</span>}
        </span>
        <span className="fp-words"><HitWords h={h} short /></span>
      </span>
    </button>
  );
});

/** The hit you're on, opened up: its words, Play, the flag action, and the
 *  flags already on it (the report's own flag cards, to edit there). */
export function CurrentHit({ h, n, state, ready, onPlay, action, flags }: {
  h: FindHit;
  n: number;
  state: HitState;
  ready: boolean;                // its file is open (the report is on it)
  onPlay: () => void;
  action: { label: string; icon: "flag" | "check" | "x"; run: () => void } | null;
  flags: ReactNode;
}) {
  return (
    <div className="fp-cur">
      <span className="fp-n">{n}</span>
      <div className="fp-body">
        <div className="fp-meta">
          <span>{h.file ?? "Not in a proofed file"}</span>
          <span>p. {h.page ?? "–"}</span>
          {h.start != null && <span className="mono">{formatTime(h.start)}</span>}
          <span className="find-grow" />
          {state.heard && h.start != null && <span className="fp-heard"><Icon name="check" size={12} /> Heard</span>}
        </div>
        <div className="fp-cur-words"><HitWords h={h} mark /></div>
        {h.start == null ? (
          <div className="fp-silent-note">
            {h.chapter_id ? "This word has no audio time, so there's nothing to play." : "No proofed file reads this page yet, so there's nothing to hear."}
          </div>
        ) : (
          <div className="fp-actions">
            <button type="button" className="btn sm primary" onClick={onPlay} disabled={!ready}>
              <Icon name="play" size={13} /> {state.heard ? "Play again" : "Play"} <kbd>Enter</kbd>
            </button>
            {action && (
              <button type="button" className={`btn sm fp-act fp-act-${action.icon}`} onClick={action.run} disabled={!ready}>
                <Icon name={action.icon} size={13} /> {action.label} <kbd>F</kbd>
              </button>
            )}
            {!ready && <span className="muted fp-loading">Opening {h.file}…</span>}
          </div>
        )}
        {flags && <div className="fp-flags">{flags}</div>}
      </div>
    </div>
  );
}
